import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertCircle, Paperclip } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { AdminGuard } from "@/components/role-guard";
import { TableSkeleton } from "@/components/skeletons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  inboundEmailStatusLabel,
  inboundEmailStatusVariant,
} from "@/lib/inbound-email";
import { trpc, withErrorToast } from "@/utils/trpc";

function formatReceived(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return format(date, "d MMM yyyy, HH:mm");
}

export default function UnresolvedEmail() {
  const navigate = useNavigate();
  const emails = useQuery(
    withErrorToast(trpc.inboundEmails.listUnresolved.queryOptions())
  );

  return (
    <AdminGuard>
      <PageContainer className="flex flex-col gap-6">
        <PageHeader
          title="Unresolved email"
          description="Inbound email that could not be matched to a site or known sender. Use the address tag and sender to work out where it belongs."
        />

        {emails.isLoading ? (
          <TableSkeleton columns={6} />
        ) : emails.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Failed to load unresolved emails</AlertTitle>
            <AlertDescription>
              {emails.error?.message ??
                "An error occurred loading unresolved emails."}
            </AlertDescription>
          </Alert>
        ) : emails.data && emails.data.length > 0 ? (
          <div className="table-panel">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-4">Received</TableHead>
                  <TableHead className="px-4">From</TableHead>
                  <TableHead className="px-4">To</TableHead>
                  <TableHead className="px-4">Subject</TableHead>
                  <TableHead className="px-4">Status</TableHead>
                  <TableHead className="px-4 text-right">Attachments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {emails.data.map((email) => (
                  <TableRow
                    key={email.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/email/${email.id}`)}
                  >
                    <TableCell className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                      {formatReceived(email.receivedAt)}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm">
                      <span className="block max-w-[14rem] truncate">
                        {email.from ?? "Unknown sender"}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-muted-foreground">
                      <span className="block max-w-[16rem] truncate">
                        {email.to ?? "—"}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm">
                      <span className="block max-w-[16rem] truncate">
                        {email.subject || (
                          <span className="text-muted-foreground">
                            (no subject)
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <Badge variant={inboundEmailStatusVariant(email.status)}>
                        {inboundEmailStatusLabel(email.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm">
                      {email.attachmentCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Paperclip className="h-3.5 w-3.5" />
                          {email.attachmentCount}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Empty className="border-dashed p-8">
            <EmptyTitle>Nothing unresolved</EmptyTitle>
            <EmptyDescription>
              Every inbound email has matched a site or sender. Unmatched mail
              will appear here for triage.
            </EmptyDescription>
          </Empty>
        )}
      </PageContainer>
    </AdminGuard>
  );
}
